import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import type { BadgeOption } from '@/components/table/table.types';
import { cn } from '@/utils/style';

export enum BadgeColor {
  GRAY = 'gray',
  DARK_GRAY = 'dark-gray',
  GREEN = 'green',
  BLUE = 'blue',
  RED = 'red',
  ORANGE = 'orange',
  PURPLE = 'purple',
}

export const badgeColorDotClasses: Record<BadgeColor, string> = {
  [BadgeColor.GRAY]: 'bg-gray-500',
  [BadgeColor.DARK_GRAY]: 'bg-gray-700',
  [BadgeColor.GREEN]: 'bg-green-600',
  [BadgeColor.BLUE]: 'bg-blue-600',
  [BadgeColor.RED]: 'bg-red-600',
  [BadgeColor.ORANGE]: 'bg-orange-500',
  [BadgeColor.PURPLE]: 'bg-purple-600',
};

export const badgeColorTextClasses: Record<BadgeColor, string> = {
  [BadgeColor.GRAY]: 'text-gray-700',
  [BadgeColor.DARK_GRAY]: 'text-gray-700',
  [BadgeColor.GREEN]: 'text-green-700',
  [BadgeColor.BLUE]: 'text-blue-700',
  [BadgeColor.RED]: 'text-red-700',
  [BadgeColor.ORANGE]: 'text-orange-700',
  [BadgeColor.PURPLE]: 'text-purple-700',
};

// A fragment: expects a `flex items-center gap-2` parent, as column-filter options and
// `CommandItem` rows already are.
export function BadgeOptionLabel({ color, label }: { color: BadgeColor; label: string }) {
  return (
    <>
      <span className={cn('size-2 shrink-0 rounded-full', badgeColorDotClasses[color])} />
      <span className={cn('text-sm truncate flex-1', badgeColorTextClasses[color])}>{label}</span>
    </>
  );
}

interface BadgeProps extends React.ComponentProps<'span'> {
  asChild?: boolean;
  color?: BadgeColor;
  icon?: React.ReactNode;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, color = BadgeColor.GRAY, asChild = false, icon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';

    return (
      <Comp
        ref={ref}
        data-slot="badge"
        className={cn(
          'inline-flex items-center justify-center rounded-full border-[1.5px] px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
          className,
          {
            'bg-gray-50 border-gray-100 text-gray-700': color === BadgeColor.GRAY,
            'bg-gray-500 border-gray-600 text-white': color === BadgeColor.DARK_GRAY,
            'bg-green-50 border-green-100 text-green-700': color === BadgeColor.GREEN,
            'bg-blue-50 border-blue-100 text-blue-700': color === BadgeColor.BLUE,
            'bg-red-50 border-red-100 text-red-700': color === BadgeColor.RED,
            'bg-orange-50 border-orange-200 text-orange-700': color === BadgeColor.ORANGE,
            'bg-purple-50 border-purple-200 text-purple-700': color === BadgeColor.PURPLE,
          }
        )}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {icon}
            {children}
          </>
        )}
      </Comp>
    );
  }
);
Badge.displayName = 'Badge';

export function OptionBadge<T>({ options, value }: { options: BadgeOption<T>[]; value: T | null }) {
  const option = options.find((candidate) => candidate.value === value);
  if (!option) return '-';
  return <Badge color={option.color}>{option.label}</Badge>;
}
